import type { WeeklyReviewContent } from '@onflow/shared';
import { useState } from 'react';

export interface ReceiptsDietBlockProps {
  content: WeeklyReviewContent;
}

/**
 * Section 4a of /review V2 (spec §5.Section 4 → 4a). The 5/7 adherence
 * headline relocates from the page top into this collapsible. Renders the
 * kcal/protein/dayMet data already on /v1/reviews/current.perDay; foods
 * count + slots-missed count are spec items that have no data source yet
 * (see plan §"Spec conflicts (resolved with user)" #2) — deferred.
 */
export function ReceiptsDietBlock({ content }: ReceiptsDietBlockProps) {
  const [open, setOpen] = useState(false);
  const { daysMet, daysLogged, perDay } = content.adherence;
  const noLogs = daysLogged === 0;
  const summary = noLogs ? '0 of 7 days logged yet' : `${daysMet} of 7 days on plan`;

  return (
    <article className="review-receipt">
      <button
        type="button"
        className="review-receipt__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="review-receipt__title">Diet</span>
        <span className="review-receipt__summary">{summary}</span>
        <span className="review-receipt__chevron" aria-hidden>
          ▾
        </span>
      </button>

      {open ? (
        <div className="review-receipt__body">
          {noLogs ? (
            <p className="review-receipt__empty">
              Log meals from your daily mission to see adherence here.
            </p>
          ) : (
            <ul className="review-perday">
              {perDay.map((d) => (
                <li
                  key={d.date}
                  className={`review-perday__row ${d.dayMet ? 'review-perday__row--met' : ''}`}
                >
                  <span className="review-perday__date">{formatWeekday(d.date)}</span>
                  <span className="review-perday__metric">
                    {Math.round(d.actualKcal)} / {d.targetKcal} kcal
                  </span>
                  <span className="review-perday__metric">
                    {Math.round(d.actualProteinG)} / {d.targetProteinG} g protein
                  </span>
                  <span
                    className="review-perday__indicator"
                    aria-label={d.dayMet ? 'Met target' : 'Did not meet target'}
                  >
                    {d.dayMet ? '✓' : '·'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="review-receipt__footnote">
            Within ±15% kcal AND ±15g protein of target counts as on-plan.
          </p>
        </div>
      ) : null}
    </article>
  );
}

function formatWeekday(yyyyMmDd: string): string {
  const [y, m, d] = yyyyMmDd.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', timeZone: 'UTC' });
}
